/**
 * AuthCallbackPage — handles the Work OS OAuth redirect.
 *
 * After Work OS redirects back to /auth/callback?code=...&state=...,
 * this page exchanges the code for tokens via POST /api/auth/callback,
 * then redirects to the main application.
 *
 * On error it shows a friendly message and offers a retry link.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const exchange = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');

      if (!code || !state) {
        setError('Missing authorization code or state parameter.');
        return;
      }

      try {
        const res = await fetch('/api/auth/callback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code, state }),
        });

        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { message?: string };
          setError(data.message ?? 'Authentication failed. Please try again.');
          return;
        }

        // Refresh auth state then navigate to the original page (or home)
        await refreshAuth();
        const returnTo = sessionStorage.getItem('auth_return_to') ?? '/';
        sessionStorage.removeItem('auth_return_to');
        // /oauth/* is answered by the server, not routed by the SPA: load it as a page.
        if (returnTo.startsWith('/oauth/')) {
          window.location.assign(returnTo);
          return;
        }
        navigate(returnTo, { replace: true });
      } catch {
        setError('A network error occurred. Please check your connection and try again.');
      }
    };

    void exchange();
  }, [navigate, refreshAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-warm-md border border-neutral-200 p-8 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">Authentication failed</h2>
          <p className="text-sm text-neutral-500 mb-6">{error}</p>
          <a
            href="/login"
            className="inline-flex items-center px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white text-sm font-medium rounded-lg transition"
          >
            Try again
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-coral-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-neutral-500">Signing you in…</p>
      </div>
    </div>
  );
}
